import { ItemListContent, userInteraction } from "@eusoft/webapp-framework";
import { EmptyView } from "@eusoft/webapp-framework";
import { FieldSearchMode, FilterEditor } from "@eusoft/webapp-framework/components/FilterEditor";
import { content, enumItemsSource, formatText, MaterialIcon, pickItemAsync } from "@eusoft/webapp-ui";
import { CreateSalePointPage, EditSalePointPage } from "./EditSalePoint";
import { navigatePage } from "../../actions/Common";
import { SalePointStatus, SalePointType, SortDirection, type IListSalePoint, type ISalePointListView } from "../../entities/Commands";
import { Icons } from "../../entities/Icons";
import { formatSalePointType } from "../../helpers/Format";
import { entityItemsSource } from "../../helpers/ItemsSources";
import { executeAsync, invalidateCache } from "../../services/DataService";
import "../../helpers/Builder"
import { userSession } from "../../services/UserSession";
import { context } from "../../services/Context";
import { WithContext } from "../../services/WithContext";

export interface IListSalePointArgs {
}


const createFilter = () => new FilterEditor<IListSalePoint, ISalePointListView>({
    fields: {

        status: {
            multipleValues: true,
            dataType: "enum",
            searchMode: FieldSearchMode.Client,
            valuesSource: enumItemsSource(SalePointStatus, "sps"),
            icon: <MaterialIcon name="chronic" />,
            color: "#FF5722", // deep-orange 500
        },
        type: {
            multipleValues: true,
            dataType: "enum",
            searchMode: FieldSearchMode.Client,
            valuesSource: enumItemsSource(SalePointType),
            icon: <MaterialIcon name="category" />,
            color: "#3F51B5", // indigo 500
        },
        merchantId: {
            label: "merchant",
            color: "#9C27B0", // purple 500
            valuesSource: entityItemsSource("ListMerchant"),
            multipleValues: false,
            searchMode: FieldSearchMode.Client,
            icon: Icons["merchant"],
        },
    },
    renderMode: "field",
    matchFields: a => [a.name],
    queryField: "query",
});



async function changeStatus(item: ISalePointListView) {

    const newValue = await pickItemAsync({
        value: item.status,
        itemsSource: enumItemsSource(SalePointStatus, "sps")
    });

    if (newValue === undefined || newValue == item.status)
        return;

    const res = await executeAsync("SetSalePointStatus", {
        status: newValue,
        id: item.id
    });

    invalidateCache("ListSalePoint");

    return false;
}

async function deleteSalePointAsync(item: ISalePointListView) {

    invalidateCache("ListSalePoint");

    let askArchive = false;

    let res = await executeAsync("DeleteSalePoint", { id: item.id }, {
        onError: (_, code) => {
            if (code == "err-in-use") {
                askArchive = true;
                return true;
            }
                
        }
    });

    if (askArchive && item.status != SalePointStatus.Archived) {

        if (!await userInteraction.confirmAsync(
            formatText("warn-no-delete-archive", formatText("sale-point")), "confirm"))
            return;

        res = await executeAsync("SetSalePointStatus", { id: item.id, status: SalePointStatus.Archived }, {
            onError: ex => {
                if (ex.message == "err-in-use") {
                    askArchive = true;
                    return true;
                }

            }
        });
    }

    return res;
}

export const ListSalePointPage = ItemListContent.builder<ISalePointListView, IListSalePoint>() 

    .info({
        name: "sale-point-list",
        route: "/sale-point",
        icon: Icons["sale-point"]
    })

    .feature(WithContext("sale-circuit"))

    .resource("SalePoint")

    .options({

        canAdd: () => userSession.hasPermission("SaleCircuit", "write"),

        title: "sale-point-list",
        style: ["panel", "round-item"],
        confirmDeleteMessage: "msg-confirm-delete",
        itemsSource: entityItemsSource("ListSalePoint", true),
        itemAddContent: CreateSalePointPage.factory,
        itemEditContent: item => content(EditSalePointPage, { id: item.id, listItem: item }),
        deleteItemAsync: deleteSalePointAsync,
        pageSize: 50,
        filterEditor: createFilter,
        prepareFilter: (curFilter, offset, limit) => ({
            saleCircuitId: context?.saleCircuit?.id,
            ...curFilter,
            sort: {
                property: "Name",
                direction: SortDirection.Ascending
            },
            pagination: {
                limit,
                offset
            }
        }),
        itemStyle: item => item.status == SalePointStatus.Archived ? "archived" : undefined,
        itemActions: (item, res) => {
            res.push({
                name: "status",
                text: "status",
                priority: "secondary",
                icon: <MaterialIcon name="chronic" />,
                canExecute: ctx => userSession.hasPermission("SalePoint", "write",ctx.target?.id),
                executeAsync: ctx => changeStatus(ctx.target)
            })

        },
        emptyView: <EmptyView
            iconName="point_of_sale"
            addLabel="create-now"
            message="msg-no-sale-point"
            addAction={() => navigatePage("sale-point-create")}
        />,
        columns: [{
            value: a => a.name,
            priority: "primary",
            header: "name",
        },
        {
            value: a => formatSalePointType(a.type),
            priority: "secondary",
            header: "type",
        },{
            value: a => a.merchant.name,
            priority: "secondary",
            header: "merchant",
        }],
        editMode: "page",
        filterMode: "tags"

    }).asContent();